import { useCallback, useState } from 'react';

// Global (not per-scene) composer setting — the dock's open/closed state and
// height should stay put across every Compose session, unlike the scene
// layout itself which is keyed by the product selection.
const STORAGE_KEY = 'composer_dock_settings';

interface StoredComposerDockSettings {
  isOpen: boolean;
  heightRatio: number;
}

const DEFAULT_HEIGHT_RATIO = 0.5;
const MIN_HEIGHT_RATIO = 0.12;
const MAX_HEIGHT_RATIO = 0.85;

function clampHeightRatio(value: number): number {
  return Math.min(Math.max(value, MIN_HEIGHT_RATIO), MAX_HEIGHT_RATIO);
}

function isStoredComposerDockSettings(
  value: unknown
): value is StoredComposerDockSettings {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.isOpen === 'boolean' &&
    typeof candidate.heightRatio === 'number'
  );
}

function readStoredSettings(): StoredComposerDockSettings {
  if (typeof window === 'undefined') {
    return { isOpen: true, heightRatio: DEFAULT_HEIGHT_RATIO };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (isStoredComposerDockSettings(parsed)) {
      return {
        isOpen: parsed.isOpen,
        heightRatio: clampHeightRatio(parsed.heightRatio)
      };
    }
  } catch (error) {
    console.error(
      '[useComposerDockHeight] failed to read stored settings',
      error
    );
  }

  return { isOpen: true, heightRatio: DEFAULT_HEIGHT_RATIO };
}

function writeStoredSettings(settings: StoredComposerDockSettings): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('[useComposerDockHeight] failed to persist settings', error);
  }
}

// Height is stored as a ratio of the dock's parent (not raw pixels) so the
// persisted size still makes sense across different modal/viewport sizes.
function useComposerDockHeight() {
  const [{ isOpen, heightRatio }, setSettings] = useState(readStoredSettings);

  const setIsOpen = useCallback((nextIsOpen: boolean) => {
    setSettings((prev) => {
      const next = { ...prev, isOpen: nextIsOpen };
      writeStoredSettings(next);
      return next;
    });
  }, []);

  const setHeightRatio = useCallback((nextHeightRatio: number) => {
    setSettings((prev) => {
      const next = {
        ...prev,
        heightRatio: clampHeightRatio(nextHeightRatio)
      };
      writeStoredSettings(next);
      return next;
    });
  }, []);

  return {
    isDockOpen: isOpen,
    setIsDockOpen: setIsOpen,
    dockHeightRatio: heightRatio,
    setDockHeightRatio: setHeightRatio
  };
}

export default useComposerDockHeight;
export { DEFAULT_HEIGHT_RATIO, MIN_HEIGHT_RATIO, MAX_HEIGHT_RATIO };
